<template>
  <div ref="dropdownContainer" class="relative">
    <button
      ref="dropdownToggle"
      class="flex items-center justify-between px-4 py-2 font-bold w-full text-left border-b border-rhino/25 transition-colors duration-200 tracking-wider"
      :class="{
        'rounded-t': dropdownMenuShown,
      }"
      type="button"
      :aria-expanded="dropdownMenuShown"
      data-testid="select-dropdown"
      @click="toggleDropdown"
    >
      <slot>{{ displayLabel }}</slot>
      <fa-icon
        :icon="faChevronDown"
        class="ms-2 size-4 text-rhino pointer-events-none transition-transform duration-200 inline-block"
        :class="{ 'rotate-180': dropdownMenuShown }"
      />
    </button>

    <div
      v-show="dropdownMenuShown"
      class="absolute left-0 right-0 z-1000 mt-0 block rounded-b bg-depth-2 overflow-hidden"
    >
      <div
        v-for="item in toDisplayItems"
        :key="item.key"
        class="block px-4 py-2 cursor-pointer text-mouse hover:text-white hover:bg-rhino/5 transition-colors duration-200"
        :data-testid="item.key"
        :class="itemClass"
        @click="onItemSelected(item)"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import type SelectItem from '~/components/form/dropdown/types';

const props = withDefaults(
  defineProps<{
    label?: string;
    selectLabel?: string;
    value?: SelectItem | string | number | null;
    modelValue?: SelectItem | string | number | null;
    items?: Array<SelectItem | string>;
    itemClass?: string;
  }>(),
  {
    label: '',
    selectLabel: '',
    value: null,
    modelValue: null,
    items: () => [],
    itemClass: '',
  },
);

const emit = defineEmits<{
  change: [value: SelectItem];
  'update:modelValue': [value: SelectItem];
  'dropdown-shown': [];
  'dropdown-hidden': [];
}>();

const assignItems = () =>
  props.items.map((i) => (typeof i === 'string' ? { key: i, label: i } : i));

const internalItems = ref<Array<SelectItem>>(assignItems());

const currentValue = computed(() => props.modelValue ?? props.value);

const findItem = (
  val: SelectItem | string | number | null,
): SelectItem | null => {
  if (val == null) return null;
  if (typeof val === 'object' && 'key' in val) return val as SelectItem;

  const items = internalItems.value;
  const found = items.find(
    (i) => i.key === val || i.key.toString() === val.toString(),
  );
  if (found) return found;

  // Fallback for primitive values that are not in items yet
  return { key: val.toString(), label: val.toString() };
};

const internalValue = ref<SelectItem | null>(findItem(currentValue.value));

watch(currentValue, (newValue) => {
  internalValue.value = findItem(newValue);
});

watch(
  () => props.items,
  () => {
    internalItems.value = assignItems();
    internalValue.value = findItem(currentValue.value);
  },
);

const toDisplayItems = computed(() =>
  internalValue.value == null
    ? internalItems.value
    : internalItems.value.filter((i) => internalValue.value?.key !== i.key),
);

const dropdownMenuShown = ref(false);
const dropdownContainer = ref<HTMLElement | null>(null);

const toggleDropdown = () => {
  dropdownMenuShown.value = !dropdownMenuShown.value;
  if (dropdownMenuShown.value) {
    emit('dropdown-shown');
  } else {
    emit('dropdown-hidden');
  }
};

const closeDropdown = () => {
  if (dropdownMenuShown.value) {
    dropdownMenuShown.value = false;
    emit('dropdown-hidden');
  }
};

const onItemSelected = (entry: SelectItem) => {
  internalValue.value = entry;
  emit('change', entry);
  emit('update:modelValue', entry);
  closeDropdown();
};

const displayLabel = computed<string>(() => {
  if (internalValue.value && internalValue.value.label) {
    return internalValue.value.label;
  }
  return props.selectLabel && props.selectLabel.length > 0
    ? props.selectLabel
    : 'Select...';
});

const handleClickOutside = (event: MouseEvent) => {
  if (
    dropdownContainer.value &&
    !dropdownContainer.value.contains(event.target as Node) &&
    dropdownMenuShown.value
  ) {
    closeDropdown();
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && dropdownMenuShown.value) {
    closeDropdown();
  }
};

const addDropdownListeners = () => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleEscape);
};

const removeDropdownListeners = () => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleEscape);
};

watch(dropdownMenuShown, (shown) => {
  if (shown) {
    addDropdownListeners();
  } else {
    removeDropdownListeners();
  }
});

onUnmounted(() => {
  removeDropdownListeners();
});
</script>
